/* eslint-disable @typescript-eslint/no-use-before-define */
import * as React from 'react';
import {
  Block,
  Tiles,
  NewTable,
  Tile,
  useTableVirtualizer,
  useWindowTableVirtualizer,
  VStack,
  Tag,
  HStack,
} from '@carta/ink';

type TableSettings = {
  floating: boolean;
  rowCount: number;
  big: number;
  small: number;
  smallCount: number;
  frozenColumnCount: number;
  twiddle: boolean;
  overscan: number;
  tables: Record<string, boolean>;
};

type RowProps = ReturnType<ReturnType<typeof useTableVirtualizer>['getRowProps']> & { onClick?: () => void };

type VirtualizerType = ReturnType<typeof useTableVirtualizer> | ReturnType<typeof useWindowTableVirtualizer>;

type TableContextType = {
  frozen: number;
  big: number;
  small: number;
  columns: number[];
  twiddle: boolean;
  virtualizer?: VirtualizerType;
};

const scrollUtils = {
  horizontal: (node: Element) => {
    if (!node) return;
    const max = node.scrollWidth - node.clientWidth;
    const toLeft = node.scrollLeft;
    node.scrollTo({ left: toLeft > max - toLeft ? 0 : max, behavior: 'smooth' });
  },

  vertical: (node: Element) => {
    if (!node) return;
    const max = node.scrollHeight - node.clientHeight;
    const toTop = node.scrollTop;
    node.scrollTo({ top: toTop > max - toTop ? 0 : max, behavior: 'smooth' });
  },
};

const domUtils = {
  findTextNode: (el: HTMLElement) => {
    const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
    walker.nextNode();
    return walker.currentNode;
  },

  getTableElement: {
    regular: () => document.querySelector('#regular div')!,
    container: () => document.querySelector('#container div div')!,
    page: () => document.scrollingElement!,
    full: () => document.querySelector('#full div')!,
    window: () => document.querySelector('#window div div')!,
  },
};

const TableContext = React.createContext<TableContextType | null>(null);

const NumberAdjuster = ({ label, value, property, steps = [-5, -1, 1, 5], shortcuts = [] as string[], onChange }) => (
  <span>
    {label}
    <Tag.Container>
      {steps.slice(0, 2).map((step, idx) => {
        return (
          <Tag data-key={shortcuts[idx]} key={`${property}-${idx}`} onClick={() => onChange(property, value + step)}>
            {step > 0 ? `+${step}` : step}
          </Tag>
        );
      })}
      <Tag variant="flex-blue-light">{value}</Tag>
      {steps.slice(2).map((step, idx) => {
        return (
          <Tag
            data-key={shortcuts[idx + 2]}
            key={`${property}-${idx + 2}`}
            onClick={() => onChange(property, value + step)}
          >
            {step > 0 ? `+${step}` : step}
          </Tag>
        );
      })}
    </Tag.Container>
  </span>
);

const ControlPanel = ({ settings, setSettings }) => {
  const floatingPanelStyle = {
    display: settings.floating ? 'block' : 'none',
    position: 'fixed',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    boxShadow: `
      4px 8px 8px hsl(220deg 60% 50% / 0.2),
      8px 16px 16px hsl(220deg 60% 50% / 0.2),
      16px 32px 32px hsl(220deg 60% 50% / 0.2)`,
    zIndex: 1000,
  } as React.CSSProperties;

  const updateSetting = (property, value) => {
    setSettings({ ...settings, [property]: value });
  };

  const toggleTable = tableKey => {
    setSettings({
      ...settings,
      tables: {
        ...settings.tables,
        [tableKey]: !settings.tables[tableKey],
      },
    });
  };

  return (
    <div style={floatingPanelStyle}>
      <Tile id="floating">
        <HStack>
          {/* Left column */}
          <VStack align="left">
            <span>
              tables
              <Tag.Container>
                {Object.entries(settings.tables).map(([key, value], index) => (
                  <Tag
                    data-key={index + 1}
                    id={`toggle-${key}`}
                    key={`toggle-${key}`}
                    variant={`flex-blue-${value ? 'light' : 'medium'}`}
                    onClick={() => toggleTable(key)}
                  >
                    {key}
                  </Tag>
                ))}
              </Tag.Container>
            </span>

            <NumberAdjuster
              label="columns"
              value={settings.smallCount}
              property="smallCount"
              shortcuts={['Q', 'q', 'w', 'W']}
              onChange={updateSetting}
            />

            <NumberAdjuster
              label="rows"
              value={settings.rowCount}
              property="rowCount"
              shortcuts={['A', 'a', 's', 'S']}
              onChange={updateSetting}
            />

            <NumberAdjuster
              label="big column (px)"
              value={settings.big}
              property="big"
              steps={[-50, -10, 10, 50]}
              shortcuts={['E', 'e', 'r', 'R']}
              onChange={updateSetting}
            />

            <NumberAdjuster
              label="small column (px)"
              value={settings.small}
              property="small"
              steps={[-50, -10, 10, 50]}
              shortcuts={['D', 'd', 'f', 'F']}
              onChange={updateSetting}
            />
          </VStack>

          {/* Right column */}
          <VStack align="left">
            <span>
              frozen
              <Tag.Container>
                <Tag data-key="[" onClick={() => updateSetting('frozenColumnCount', settings.frozenColumnCount - 1)}>
                  -1
                </Tag>
                <Tag variant="flex-blue-light">{settings.frozenColumnCount + 1}</Tag>
                <Tag data-key="]" onClick={() => updateSetting('frozenColumnCount', settings.frozenColumnCount + 1)}>
                  +1
                </Tag>
              </Tag.Container>
            </span>

            <span>
              scrolling
              <Tag.Container>
                <Tag
                  data-key="o"
                  onClick={() => {
                    scrollUtils.vertical(domUtils.getTableElement.regular());
                    scrollUtils.vertical(domUtils.getTableElement.container());
                  }}
                >
                  x axis
                </Tag>
                <Tag
                  data-key="p"
                  onClick={() => {
                    scrollUtils.horizontal(domUtils.getTableElement.regular());
                    scrollUtils.horizontal(domUtils.getTableElement.container());
                  }}
                >
                  y axis
                </Tag>
              </Tag.Container>
            </span>

            <span>
              window
              <Tag.Container>
                <Tag data-key="k" onClick={() => scrollUtils.vertical(domUtils.getTableElement.page())}>
                  x axis
                </Tag>
                <Tag
                  data-key="l"
                  onClick={() => {
                    scrollUtils.horizontal(domUtils.getTableElement.full());
                    scrollUtils.horizontal(domUtils.getTableElement.window());
                  }}
                >
                  y axis
                </Tag>
              </Tag.Container>
            </span>

            <span>
              twiddle
              <Tag.Container>
                <Tag data-key="t" onClick={() => updateSetting('twiddle', !settings.twiddle)}>
                  twiddle
                </Tag>
              </Tag.Container>
            </span>

            <NumberAdjuster
              label="overscan"
              value={settings.overscan}
              property="overscan"
              shortcuts={['<', ',', '.', '>']}
              onChange={updateSetting}
            />
          </VStack>
        </HStack>
        <hr />
        <p>Press Esc to toggle this panel, or hold Shift to see other shortcuts.</p>
      </Tile>
    </div>
  );
};

const DEFAULT_SETTINGS: TableSettings = {
  floating: true,
  rowCount: 50,
  big: 200,
  small: 100,
  smallCount: 10,
  frozenColumnCount: 0,
  twiddle: true,
  overscan: 5,
  tables: {
    regular: true,
    container: true,
    full: true,
    window: true,
  },
};

function NewTableScrollExpose() {
  useKeyboardShortcuts();
  const [settings, setSettings] = React.useState<TableSettings>(DEFAULT_SETTINGS);

  const data = React.useMemo(() => [...Array(settings.rowCount).keys()], [settings.rowCount]);
  const columns = React.useMemo(() => [...Array(settings.smallCount).keys()].map(e => e + 1), [settings.smallCount]);

  const virtualizerOptions = {
    overscan: settings.overscan,
    getTwiddleRowCount: () => (settings.twiddle ? 5 : 0),
    getItemKey: (index: number) => index,
  };

  const virtualizer = useTableVirtualizer(data, virtualizerOptions);
  const windowVirtualizer = useWindowTableVirtualizer(data, virtualizerOptions);

  // Toggle floating panel with Escape key
  React.useEffect(() => {
    const handleKeypress = (e: KeyboardEvent) => {
      if (e.target !== document.body || e.key !== 'Escape') return;
      setSettings(s => ({ ...s, floating: !s.floating }));
    };

    window.addEventListener('keydown', handleKeypress);
    return () => window.removeEventListener('keydown', handleKeypress);
  }, []);

  const tableContextValue = {
    frozen: settings.frozenColumnCount,
    big: settings.big,
    small: settings.small,
    columns,
    twiddle: settings.twiddle,
  };

  return (
    <>
      <ControlPanel settings={settings} setSettings={setSettings} />
      <Tiles columns={[1, 2]}>
        {settings.tables.regular && (
          <Tile height="content">
            <Block title="Regular table">
              <div id="regular">
                <TableContext.Provider value={tableContextValue}>
                  <NewTable height="400px">
                    <RegularTable data={data} />
                  </NewTable>
                </TableContext.Provider>
              </div>
            </Block>
          </Tile>
        )}

        {settings.tables.container && (
          <Tile height="content">
            <Block title="Virtual container">
              <div id="container">
                <TableContext.Provider value={{ ...tableContextValue, virtualizer }}>
                  <NewTable.VirtualWrapper {...virtualizer.getWrapperProps()}>
                    <NewTable height="400px">
                      <VirtualTable />
                    </NewTable>
                  </NewTable.VirtualWrapper>
                </TableContext.Provider>
              </div>
            </Block>
          </Tile>
        )}

        {settings.tables.full && (
          <Tile height="content">
            <Block title="Full-height table">
              <div id="full">
                <TableContext.Provider value={tableContextValue}>
                  <NewTable offsetStickyTop={0}>
                    <RegularTable data={data} />
                  </NewTable>
                </TableContext.Provider>
              </div>
            </Block>
          </Tile>
        )}

        {settings.tables.window && (
          <Tile height="content">
            <Block title="Virtual window">
              <div id="window">
                <TableContext.Provider value={{ ...tableContextValue, virtualizer: windowVirtualizer }}>
                  <NewTable.VirtualWrapper {...windowVirtualizer.getWrapperProps()}>
                    <NewTable offsetStickyTop={0}>
                      <VirtualTable />
                    </NewTable>
                  </NewTable.VirtualWrapper>
                </TableContext.Provider>
              </div>
            </Block>
          </Tile>
        )}
      </Tiles>
    </>
  );
}

const useTableContext = () => {
  const context = React.useContext(TableContext);
  if (!context) {
    throw new Error('useTableContext must be used within a TableContextProvider');
  }
  return context;
};

const useKeyboardShortcuts = () => {
  const [shortcutsVisible, setShortcutsVisible] = React.useState(false);

  React.useEffect(() => {
    const handleShift = (e: KeyboardEvent) => {
      if ((e.target as HTMLElement).nodeName === 'text') return;
      if (e.key === 'Shift') setShortcutsVisible(e.shiftKey);
    };

    const handleKeypress = (e: KeyboardEvent) => {
      if ((e.target as HTMLElement).nodeName === 'text') return;
      if (e.metaKey || e.ctrlKey) return;

      const key = e.key;
      const buttonSelector = `[data-key="${key}"] button, [data-key="${key}"] [role="button"]`;
      const button = document.querySelector(buttonSelector) as HTMLButtonElement;

      if (!button) {
        console.log(`No button found for [data-key="${key}"]`);
        return;
      }

      button.style.transition = 'background-color 0.3s ease';
      button.style.backgroundColor = '#90ddff';

      button.dispatchEvent(new MouseEvent('click', { bubbles: true, composed: true }));
      console.log('Dispatched click event on', button);

      setTimeout(() => {
        button.style.backgroundColor = '';
        button.style.transition = '';
      }, 300);
    };

    window.addEventListener('keydown', handleKeypress);
    window.addEventListener('keyup', handleShift);
    window.addEventListener('keydown', handleShift);

    return () => {
      window.removeEventListener('keydown', handleKeypress);
      window.removeEventListener('keyup', handleShift);
      window.removeEventListener('keydown', handleShift);
    };
  }, []);

  React.useEffect(() => {
    window.requestAnimationFrame(() => {
      document.querySelectorAll('[data-key]').forEach(element => {
        const button = element as HTMLButtonElement;
        const textNode = domUtils.findTextNode(button)!;
        const dataKey = button.getAttribute('data-key')!;
        const dataText = button.getAttribute('data-text')!;

        if (shortcutsVisible) {
          button.style.width = `${button.getBoundingClientRect().width}px`;
          button.style.justifyContent = 'space-evenly';
          button.setAttribute('data-text', textNode?.textContent || '');
          textNode.textContent = dataKey;
        } else if (dataText) {
          button.style.width = '';
          button.style.justifyContent = '';
          button.setAttribute('data-text', '');
          textNode.textContent = dataText;
        }
      });
    });
  }, [shortcutsVisible]);
};

const RegularTable = ({ data }) => {
  const { twiddle } = useTableContext();

  return (
    <>
      <Head />
      <NewTable.Body>
        {data.map((i, index) =>
          twiddle ? (
            <NewTable.Twiddle key={i} id={i}>
              {({ toggle, open }) => (
                <>
                  <Row index={index} onClick={toggle} />
                  {open && <ChildRow index={i} />}
                </>
              )}
            </NewTable.Twiddle>
          ) : (
            <Row onClick={() => null} key={i} index={index} />
          ),
        )}
      </NewTable.Body>
      <Foot />
    </>
  );
};

const VirtualTable = () => {
  const { twiddle, virtualizer, big, small, columns, frozen } = useTableContext();

  if (!virtualizer) return null;

  if (!twiddle) {
    return (
      <>
        <Head />
        <NewTable.Body {...virtualizer.getBodyProps()}>
          {virtualizer.getVirtualItems().map(i => (
            <NewTable.Row {...virtualizer.getRowProps(i)}>
              {/* Properly destructured context values */}
              <NewTable.Cell width={`${big}px`} freeze={frozen >= 0}>
                Row {i.index + 1}
              </NewTable.Cell>
              {columns.map((n, colIndex) => (
                <NewTable.Cell key={n} width={`${small}px`} freeze={colIndex < frozen}>
                  {(i.index + 1) * n}%
                </NewTable.Cell>
              ))}
            </NewTable.Row>
          ))}
        </NewTable.Body>
        <Foot />
      </>
    );
  }

  return (
    <>
      <Head />
      <NewTable.Body {...virtualizer.getBodyProps()}>
        {virtualizer.getVirtualItems().map(i => {
          const row = virtualizer.getRowItem(i);

          if (i.type === 'parent') {
            return (
              <NewTable.Twiddle key={`p-${i.key}`} id={row as number}>
                {({ toggle }) => (
                  <Row index={row as number} onClick={toggle} virtualProps={virtualizer.getRowProps(i)} />
                )}
              </NewTable.Twiddle>
            );
          }

          return (
            <ChildRow
              virtualProps={virtualizer.getRowProps(i)}
              key={`c-${i.key}`}
              index={virtualizer.getRowItem(i) as number}
              twiddleIndex={i.twiddleIndex + 1}
            />
          );
        })}
      </NewTable.Body>
      <Foot />
    </>
  );
};

const Foot = () => {
  const { big, small, columns, frozen, twiddle } = useTableContext();

  return (
    <NewTable.Foot>
      <NewTable.Row preset="totals">
        {twiddle && <NewTable.Cell width="40px" freeze={frozen >= 0} />}
        <NewTable.Cell width={`${big}px`} freeze={frozen >= 0}>
          Footer
        </NewTable.Cell>
        {columns.map((n, index) => (
          <NewTable.Cell key={n} width={`${small}px`} freeze={index < frozen}>
            {small}
          </NewTable.Cell>
        ))}
      </NewTable.Row>
    </NewTable.Foot>
  );
};

const Row = ({ index = 0, onClick, virtualProps = {} as RowProps }) => {
  const { big, small, columns, frozen, twiddle } = useTableContext();

  return (
    <NewTable.Row onClick={onClick} {...virtualProps}>
      {twiddle && (
        <NewTable.Cell preset="twiddleIcon" freeze={frozen >= 0}>
          <NewTable.Twiddle.Icon />
        </NewTable.Cell>
      )}
      <NewTable.Cell freeze={frozen >= 0} width={`${big}px`}>
        Row {index + 1}
      </NewTable.Cell>
      {columns.map((n, colIndex) => (
        <NewTable.Cell key={n} width={`${small}px}`} freeze={colIndex < frozen}>
          {(index + 1) * n}%
        </NewTable.Cell>
      ))}
    </NewTable.Row>
  );
};

const ChildRow = ({ index, twiddleIndex = 0, virtualProps = {} }) => {
  const { big, small, columns, frozen } = useTableContext();
  const words = 'did you know that the word "percent" comes from the Latin "per centum", meaning "by the hundred"?'.split(
    ' ',
  );

  return (
    <NewTable.Row {...virtualProps}>
      <NewTable.Cell freeze={frozen >= 0} width="40px" />
      <NewTable.Cell freeze={frozen >= 0} width={`${big}px`}>
        Child row {index + 1}
        {twiddleIndex > 0 ? `.${twiddleIndex}` : ''}
      </NewTable.Cell>
      {columns.map((n, colIndex) => (
        <NewTable.Cell key={n} width={`${small}px`} freeze={colIndex < frozen}>
          {words[colIndex] || '-'}
        </NewTable.Cell>
      ))}
    </NewTable.Row>
  );
};

const Head = () => {
  const { big, small, columns, frozen, twiddle } = useTableContext();

  return (
    <NewTable.Head>
      <NewTable.Row>
        {twiddle && (
          <NewTable.HeadCell preset="twiddleIcon" freeze={frozen >= 0}>
            <NewTable.Twiddle.ExpandAll data-key="g" />
          </NewTable.HeadCell>
        )}
        <NewTable.HeadCell width={`${big}px`} freeze={frozen >= 0}>
          {big}px
        </NewTable.HeadCell>
        {columns.map((n, index) => (
          <NewTable.HeadCell key={n} width={`${small}px`} freeze={index < frozen}>
            {n}.{small}px
          </NewTable.HeadCell>
        ))}
      </NewTable.Row>
    </NewTable.Head>
  );
};

export default NewTableScrollExpose;
